/**
 * Risk register (Phase 4B). Each CDW risk with what triggered it, the asset involved, when it was identified,
 * CDW's suggested next steps, the firm's own response and mitigation, the action plans addressing it, and
 * any risk acceptance. The IT lead records the response and links plans; the firm's executive approves an
 * acceptance. "Ask the assistant" opens the related control with the question ready to send.
 */
import { ACCEPTANCE_STATUS, DELIVERY_STATUS, FAMILY_LINK_LABEL } from '@grc/shared/vocabulary';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, useApi, useApiClient } from '../api';
import { acceptanceLine, formatDate, Load, PageHeader, Status } from '../components';
import { ExplainedPanel, WithPanel } from '../Explained';
import { NewPlan } from './ActionPlans';
import { NetworkBanner, RESPONSE_STATE, SeverityChip } from './FirmOverview';

interface Acceptance {
  id: number;
  status: string;
  rationale: string;
  compensatingControls: string | null;
  approverName: string;
  approverRole: string;
  decidedOn: string;
  reviewDueOn: string;
  confirmedBy: string | null;
  confirmedAt: string | null;
  createdByMe: boolean;
}

interface Risk {
  id: number;
  /** DEC-40: linked to its controls by the CDW family fallback (or of unknown origin), to review. */
  linkedByFamily?: boolean;
  code: string;
  title: string;
  scenario: string | null;
  affectedAsset: string | null;
  severity: string;
  likelihood: number | null;
  impact: number | null;
  identifiedOn: string | null;
  source: string | null;
  responseState: string | null;
  response: string | null;
  mitigation: string | null;
  responseBy: string | null;
  responseAt: string | null;
  controls: string[];
  activities: Array<{ id: number; summary: string; categories: string | null; effort: string | null; timeframe: string | null }>;
  plans: Array<{ id: number; code: string; objective: string; targetDate: string | null; deliveryStatus: string; progress: number | null }>;
  acceptances: Acceptance[];
}

interface Data {
  firmCode: string;
  canUpdate: boolean;
  isExecutive: boolean;
  viewer: 'firm' | 'network';
  measure: string;
  risks: Risk[];
}

const TIMEFRAME: Record<string, string> = { immediate: 'within 3 months', short: 'short term', mid: 'medium term', long: 'long term' };

export function FirmRisks() {
  const { firmCode } = useParams();
  const state = useApi<Data>(`/firms/${firmCode}/risks`);
  return (
    <Load state={state} what="the risk register">
      {(d) => (
        <>
          <NetworkBanner viewer={d.viewer} firmName={d.firmCode} />
          <p className="page-back">
            <Link to={`/firm/${d.firmCode}`}>Back to the dashboard</Link>
          </p>
          <PageHeader eyebrow="Your firm" title="Risks and suggested next steps" lede="What the assessment found, how the firm is responding, and the plans that address each risk." />
          <WithPanel panel={<ExplainedPanel show={['status', 'risk', 'sources']} />}>
            <p className="muted small">{d.measure}</p>
            {d.risks.length === 0 && <p>No risk assessment report is in the portal yet.</p>}
            {d.risks.map((r) => (
              <RiskCard key={r.id} r={r} d={d} onChanged={state.reload} />
            ))}
          </WithPanel>
        </>
      )}
    </Load>
  );
}

function RiskCard({ r, d, onChanged }: { r: Risk; d: Data; onChanged: () => void }) {
  const [planning, setPlanning] = useState(false);
  const control = r.controls[0];
  const ask = control
    ? `/firm/${d.firmCode}/controls/${control}?ask=${encodeURIComponent(
        `Risk ${r.code} "${r.title}" has ${r.plans.length ? 'an action plan' : 'no action plan yet'}. How should we address it? Give me concrete steps, in order, and what evidence would show it is done.`,
      )}`
    : null;
  return (
    <section className={`card risk-card sev-band-${r.severity}`} aria-labelledby={`risk-${r.id}`}>
      <div className="risk-card-head">
        <SeverityChip severity={r.severity} />
        <h2 id={`risk-${r.id}`}>
          <span className="cid">{r.code}</span> {r.title}
        </h2>
        {r.linkedByFamily && <span className="chip tone-neutral link-review">{FAMILY_LINK_LABEL}</span>}
      </div>
      <p className="muted small">
        Identified {formatDate(r.identifiedOn)}
        {r.source && ` in ${r.source}`}
        {r.likelihood && r.impact ? ` · likelihood ${r.likelihood} of 5, impact ${r.impact} of 5` : ''}
      </p>
      <div className="cards two">
        <div>
          <h3>What could happen</h3>
          <p>{r.scenario ?? 'Not described in the report.'}</p>
          {r.affectedAsset && (
            <p className="small">
              <strong>Affected:</strong> {r.affectedAsset}
            </p>
          )}
          {r.controls.length > 0 && (
            <div className="tags">
              {r.controls.map((c) => (
                <Link key={c} className="tag control-tag" to={`/firm/${d.firmCode}/controls/${c}`}>
                  {c}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div>
          <h3>Suggested next steps (CDW)</h3>
          {r.activities.length ? (
            <ul>
              {r.activities.map((a) => (
                <li key={a.id}>
                  {a.summary} <span className="muted small">{[a.timeframe && TIMEFRAME[a.timeframe], a.effort && `${a.effort} effort`].filter(Boolean).join(', ')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">None listed.</p>
          )}
        </div>
      </div>

      <Response r={r} d={d} onChanged={onChanged} />

      <h3>Action plans</h3>
      {r.plans.length ? (
        <ul className="plain">
          {r.plans.map((p) => (
            <li key={p.id}>
              <strong>{p.code}</strong> {p.objective} <Status map={DELIVERY_STATUS} value={p.deliveryStatus} />{' '}
              <span className="muted small">
                {p.progress ?? 0}% done{p.targetDate && `, target ${formatDate(p.targetDate)}`}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">No action plan addresses this risk yet.</p>
      )}
      <div className="actions">
        {d.canUpdate && !planning && (
          <button className="pill secondary-dark" onClick={() => setPlanning(true)}>
            Create an action plan for this risk
          </button>
        )}
        {ask && d.canUpdate && (
          <Link className="pill assistant-pill" to={ask}>
            Ask the control assistant
          </Link>
        )}
      </div>
      {planning && <NewPlan firmCode={d.firmCode} riskId={r.id} onDone={() => (setPlanning(false), onChanged())} onCancel={() => setPlanning(false)} />}

      <Acceptances r={r} d={d} onChanged={onChanged} />
    </section>
  );
}

function Response({ r, d, onChanged }: { r: Risk; d: Data; onChanged: () => void }) {
  const call = useApiClient();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ state: r.responseState && r.responseState !== 'accepted' ? r.responseState : 'mitigating', response: r.response ?? '', mitigation: r.mitigation ?? '' });
  const [error, setError] = useState<string | null>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await call(`/firms/${d.firmCode}/risks/${r.id}/response`, { method: 'PUT', body: f });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The response did not save.');
    }
  };
  return (
    <div className="response">
      <h3>
        The firm's response <span className={`chip resp-${r.responseState ?? 'not_started'}`}>{RESPONSE_STATE[r.responseState ?? 'not_started']}</span>
      </h3>
      {!editing && (
        <>
          {r.response && <p>{r.response}</p>}
          {r.mitigation && (
            <p>
              <strong>How it will be mitigated:</strong> {r.mitigation}
            </p>
          )}
          {r.responseBy && (
            <p className="muted small">
              {r.responseBy}, {formatDate(r.responseAt)}
            </p>
          )}
          {!r.response && !r.mitigation && <p className="muted small">No response recorded yet.</p>}
          {d.canUpdate && (
            <button className="link" onClick={() => setEditing(true)} aria-label={`Record the response to ${r.code}`}>
              {r.response ? 'Update the response' : 'Record the response'}
            </button>
          )}
        </>
      )}
      {editing && (
        <form className="form" onSubmit={save} aria-label={`Response to ${r.code}`}>
          <label>
            Where it stands
            <select value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}>
              {['not_started', 'mitigating', 'mitigated'].map((s) => (
                <option key={s} value={s}>
                  {RESPONSE_STATE[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            The firm's view of the risk
            <textarea rows={3} maxLength={4000} value={f.response} onChange={(e) => setF({ ...f, response: e.target.value })} />
          </label>
          <label>
            How it will be mitigated
            <textarea rows={4} maxLength={4000} value={f.mitigation} onChange={(e) => setF({ ...f, mitigation: e.target.value })} />
          </label>
          <div className="actions">
            <button className="pill">Save the response</button>
            <button type="button" className="pill secondary-dark" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          {error && (
            <p role="alert" className="notice">
              {error}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

function Acceptances({ r, d, onChanged }: { r: Risk; d: Data; onChanged: () => void }) {
  const call = useApiClient();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ rationale: '', compensatingControls: '', approverName: '', approverRole: '', decidedOn: today, reviewDueOn: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setMessage(null);
    try {
      await fn();
      setMessage({ ok: true, text: ok });
      onChanged();
      return true;
    } catch (err) {
      setMessage({ ok: false, text: err instanceof ApiError ? err.message : 'Something went wrong.' });
      return false;
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act(() => call(`/firms/${d.firmCode}/risks/${r.id}/risk-acceptances`, { method: 'POST', body: f }), 'Recorded. It now awaits your executive’s approval.')) setOpen(false);
  };
  const review = r.controls[0]
    ? `/firm/${d.firmCode}/controls/${r.controls[0]}?ask=${encodeURIComponent(
        `Please review this risk acceptance for ${r.code} "${r.title}" as a devil's advocate, weighing the risk against the impact on our operations.\n\nRationale: ${f.rationale || '(not written yet)'}\nCompensating controls: ${f.compensatingControls || '(none yet)'}`,
      )}`
    : null;
  return (
    <div className="acceptance">
      <h3>Risk acceptance</h3>
      {r.acceptances.map((a) => (
        <div key={a.id} className="decision">
          <p>
            <Status map={ACCEPTANCE_STATUS} value={a.status} /> {acceptanceLine(a)}
          </p>
          <p className="pre">{a.rationale}</p>
          {a.compensatingControls && (
            <p className="pre">
              <strong>Compensating controls:</strong> {a.compensatingControls}
            </p>
          )}
          {a.status === 'pending_approval' && d.isExecutive && !a.createdByMe && (
            <button className="pill" onClick={() => act(() => call(`/firms/${d.firmCode}/risk-acceptances/${a.id}/approve`, { method: 'POST' }), 'Risk acceptance approved.')}>
              Approve
            </button>
          )}
        </div>
      ))}
      {message && (
        <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'ok' : 'notice'}>
          {message.text}
        </p>
      )}
      {d.canUpdate &&
        (open ? (
          <form onSubmit={submit} className="form" aria-label={`Accept ${r.code}`}>
            <label>
              Why the firm accepts this risk (business reason and decision)
              <textarea required minLength={20} maxLength={4000} rows={5} value={f.rationale} onChange={set('rationale')} />
            </label>
            <label>
              Compensating controls
              <textarea maxLength={4000} rows={3} value={f.compensatingControls} onChange={set('compensatingControls')} />
            </label>
            <div className="row">
              <label>
                Decided by
                <input required maxLength={200} value={f.approverName} onChange={set('approverName')} />
              </label>
              <label>
                Their role
                <input required maxLength={120} value={f.approverRole} onChange={set('approverRole')} placeholder="for example, Managing Partner" />
              </label>
            </div>
            <div className="row">
              <label>
                Decision date
                <input type="date" required max={today} value={f.decidedOn} onChange={set('decidedOn')} />
              </label>
              <label>
                Review by (within a year)
                <input type="date" required value={f.reviewDueOn} onChange={set('reviewDueOn')} />
              </label>
            </div>
            <div className="actions">
              <button className="pill">Record for approval</button>
              {review && (
                <Link className="pill assistant-pill" to={review}>
                  Have the assistant challenge it
                </Link>
              )}
              <button type="button" className="pill secondary-dark" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button className="link" onClick={() => setOpen(true)} aria-label={`Accept risk ${r.code}`}>
            Accept this risk instead
          </button>
        ))}
    </div>
  );
}
